import { Component, Input, Output, EventEmitter, signal, computed, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { TranslatableItem } from '@app/common-codes/components/nested-components/translatable-entity-modal/translatable-entity.interface';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { SafeHtmlPipe } from '@app/common-codes/components/directives/SafeHtmlPipe';

export interface QuizViewerResultEvent {
    passed: boolean;
    allAnswered: boolean;
    score: number;
    total: number;
    answers: { quizId: number; selectedResponseId: number; isCorrect: boolean }[];
}

@Component({
    selector: 'app-quiz-viewer',
    standalone: true,
    imports: [FormsModule, TranslatePipe, SafeHtmlPipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './quiz-viewer.component.html'
})
export class QuizViewerComponent implements OnInit, OnChanges {

    /** Quiz items (questions) avec leurs children (réponses possibles) */
    @Input({ required: true }) items: TranslatableItem[] = [];

    /** Nombre de questions à afficher (si undefined, toutes) */
    @Input() nbQuestions?: number;

    /** Afficher les questions dans un ordre aléatoire */
    @Input() random: boolean = false;

    /** typeDeTraduction pour le titre de la question (default: 1) */
    titleTranslationType: number = 1;

    responseTranslationType: number = 4;

    /** typeDeTraduction pour le contenu HTML de la question (default: 2) */
    contentTranslationType: number = 2;

    @Output() quizCompleted = new EventEmitter<QuizViewerResultEvent>();

    displayedItems: TranslatableItem[] = [];
    expandedItemId = signal<number | null>(null);
    /** Map quizId -> selectedResponseId */
    selectedAnswers = signal<Map<number, number>>(new Map());

    private currentLangKey: string = 'en';

    allAnswered = computed(() => {
        const ans = this.selectedAnswers();
        return this.displayedItems.length > 0 && this.displayedItems.every(item => ans.has(item.id));
    });

    score = computed(() => {
        const ans = this.selectedAnswers();
        let correct = 0;
        for (const item of this.displayedItems) {
            const selectedId = ans.get(item.id);
            if (selectedId != null) {
                const response = (item.children || []).find(c => c.id === selectedId);
                if (response?.isRightResponse) correct++;
            }
        }
        return correct;
    });

    passed = computed(() => {
        return this.allAnswered() && this.score() === this.displayedItems.length;
    });

    constructor(private translationService: TranslationService) { }

    ngOnInit(): void {
        this.currentLangKey = this.translationService.currentLanguage;
        this.translationService.currentLanguage$.subscribe(lang => {
            this.currentLangKey = lang;
        });
        this.buildDisplayedItems();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['items'] || changes['nbQuestions'] || changes['random']) {
            this.selectedAnswers.set(new Map());
            this.buildDisplayedItems();
        }
    }

    private buildDisplayedItems(): void {
        let questions = [...this.items];

        if (this.random) {
            questions = this.shuffle(questions);
        }

        if (this.nbQuestions && this.nbQuestions > 0 && this.nbQuestions < questions.length) {
            questions = questions.slice(0, this.nbQuestions);
        }

        this.displayedItems = questions;

        if (this.displayedItems.length > 0) {
            this.expandedItemId.set(this.displayedItems[0].id);
        }
    }

    private shuffle<T>(array: T[]): T[] {
        const arr = [...array];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    }

    getTitle(item: TranslatableItem): string {
        const translations = item.translations || [];
        let t = translations.find(
            tr => tr.typeDeTraduction === this.titleTranslationType && tr.language?.key === this.currentLangKey
        );
        if (!t && this.currentLangKey !== 'en') {
            t = translations.find(
                tr => tr.typeDeTraduction === this.titleTranslationType && tr.language?.key === 'en'
            );
        }
        if (!t) {
            t = translations.find(tr => tr.typeDeTraduction === this.titleTranslationType);
        }
        return t?.translationValue || item.description || `Question #${item.id}`;
    }

    getContent(item: TranslatableItem): string {
        const translations = item.translations || [];
        let t = translations.find(
            tr => tr.typeDeTraduction === this.contentTranslationType && tr.language?.key === this.currentLangKey
        );
        if (!t && this.currentLangKey !== 'en') {
            t = translations.find(
                tr => tr.typeDeTraduction === this.contentTranslationType && tr.language?.key === 'en'
            );
        }
        if (!t) {
            t = translations.find(tr => tr.typeDeTraduction === this.contentTranslationType);
        }
        return t?.translationValue || '';
    }

    getResponseTitle(response: TranslatableItem): string {
        const translations = response.translations || [];
        let t = translations.find(
            tr => tr.typeDeTraduction === this.responseTranslationType && tr.language?.key === this.currentLangKey
        );
        if (!t && this.currentLangKey !== 'en') {
            t = translations.find(
                tr => tr.typeDeTraduction === this.responseTranslationType && tr.language?.key === 'en'
            );
        }
        if (!t) {
            t = translations.find(tr => tr.typeDeTraduction === this.responseTranslationType);
        }
        return t?.translationValue || response.description || `Réponse #${response.id}`;
    }

    toggleExpand(itemId: number): void {
        this.expandedItemId.update(current => current === itemId ? null : itemId);
    }

    isExpanded(itemId: number): boolean {
        return this.expandedItemId() === itemId;
    }

    getSelectedAnswer(quizId: number): number | undefined {
        return this.selectedAnswers().get(quizId);
    }

    selectAnswer(quizId: number, responseId: number): void {
        this.selectedAnswers.update(current => {
            const next = new Map(current);
            next.set(quizId, responseId);
            return next;
        });

        // Auto-ouvrir la question suivante
        const currentIndex = this.displayedItems.findIndex(i => i.id === quizId);
        if (currentIndex >= 0 && currentIndex < this.displayedItems.length - 1) {
            const nextItem = this.displayedItems[currentIndex + 1];
            this.expandedItemId.set(nextItem.id);
        } else {
            this.expandedItemId.set(null);
        }

        this.emitResult();
    }

    private emitResult(): void {
        const ans = this.selectedAnswers();
        const answers = this.displayedItems.map(item => {
            const selectedId = ans.get(item.id);
            const response = (item.children || []).find(c => c.id === selectedId);
            return {
                quizId: item.id,
                selectedResponseId: selectedId ?? 0,
                isCorrect: !!response?.isRightResponse
            };
        }).filter(a => a.selectedResponseId !== 0);

        this.quizCompleted.emit({
            passed: this.passed(),
            allAnswered: this.allAnswered(),
            score: this.score(),
            total: this.displayedItems.length,
            answers
        });
    }
}